import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import {
  Box,
  CardShipment,
  FiservCard,
  FiservCardInterface,
  getUtcDate,
  Preloader,
  Text,
  useResponsive,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { DEFAULT_DATE_FORMAT, EMPTY_FIELD_VALUE } from '@/common/constants';
import { useHistory as useCustomHistory } from '@/modules/core/hooks';
import { useOrganizationOverridableProperties } from '@/modules/core/hooks/useOrganizationOverridableProperties';
import useGetFraudConfirmedShipmentItem from '@/modules/transaction/hooks/useGetFraudConfirmedShipmentItem';
import { whiteLabelingConfig } from '@/utils/whiteLabeling';

import { useSearchParams } from '../../Cards/hooks/useSearchParams';
import { useGetCardsQuery } from '../../Cards/queries/useGetCards.query';
import { CardActionsCarousel } from '../CardActionsCarousel/CardActionsCarousel';
import { useCardShipmentTrackerQuery } from '../queries';

import 'react-responsive-carousel/lib/styles/carousel.min.css';

const getParams = (cardId: string) =>
  new URLSearchParams({
    card_id: cardId,
  });

interface Props {
  employeeId: string;
  setSelectedCard?: (card: FiservCardInterface) => void;
}

const CardCarousel: React.FC<Props> = ({ employeeId, setSelectedCard }) => {
  const { push, replace } = useHistory();
  const { isMobile } = useResponsive();
  const { pathname, hash } = useLocation();
  const { observingSearchQuery } = useCustomHistory();
  const { observingOrganizationData } = useOrganizationOverridableProperties();

  const currentPath = useMemo(() => `${pathname}?${observingSearchQuery}`, [observingSearchQuery, pathname]);

  const pushWithParams = useCallback(
    (cardId: string) => {
      const params = getParams(cardId);
      push(`${currentPath}&${params.toString()}${hash}`);
    },
    [push, hash, currentPath]
  );

  const replaceWithParams = useCallback(
    (cardId: string) => {
      const params = getParams(cardId);
      replace(`${currentPath}&${params.toString()}${hash}`);
    },
    [replace, hash, currentPath]
  );

  const handlePushWithParams = useCallback(
    (_dependentId: string, cardId: string) => {
      pushWithParams(cardId);
    },
    [pushWithParams]
  );

  const { query: cardIdFromSearch } = useSearchParams('card_id');
  const { params: { cardId: cardIdFromPath } } = useRouteMatch<{ cardId?: string }>();
  const cardId = cardIdFromSearch || cardIdFromPath;

  const { cardMap, isLoading } = useGetCardsQuery(employeeId);

  const cards: FiservCardInterface[] = useMemo(() => {
    const fields: FiservCardInterface[] = [];
    Object.keys(cardMap).forEach((key) => {
      const field = cardMap[key];
      Object.keys(field).forEach((newKey) => {
        const list = _.get(field, `${newKey}`) as FiservCardInterface[];
        list.forEach((item) => {
          fields.push(item);
        });
      });
    });

    return fields;
  }, [cardMap]);

  //   const goNextHandler = useCallback(
  //     (type: 'prev' | 'next') => {
  //       if (!activeCard) return;
  //       if (type === 'next' && cards[selectedItemIndex + 1]) {
  //         pushWithParams(cards[selectedItemIndex + 1].holder.dependentId, cards[selectedItemIndex + 1].id);
  //       }

  //       if (type === 'prev' && cards[selectedItemIndex - 1]) {
  //         pushWithParams(cards[selectedItemIndex - 1].holder.dependentId, cards[selectedItemIndex - 1].id);
  //       }
  //     },
  //     [activeCard, cards, selectedItemIndex, pushWithParams]
  //   );
  const selectedItemIndex = useMemo(() => {
    const index = cards.findIndex((card) => card.id === cardId);
    return index > -1 ? index : 0;
  }, [cardId, cards]);

  const currentCard = useMemo(() => cards?.[selectedItemIndex], [cards, selectedItemIndex]);
  const { isLoading: isTrackerLoading, data: shipmentList } = useCardShipmentTrackerQuery({
    id: currentCard?.id,
  });

  const { fraudConfirmedShipment } = useGetFraudConfirmedShipmentItem({
    fraudConfirmed: currentCard?.fraudConfirmed,
    fraudConfirmedDate: currentCard?.fraudConfirmedDate,
  });

  useEffect(() => {
    if (currentCard?.id) {
      setSelectedCard?.(currentCard);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCard]);
  
  useEffect(() => {
    if (cards.length && selectedItemIndex >= 0) {
      const nextCardId = cards[selectedItemIndex].id;
      if (nextCardId !== cardId) {
        replaceWithParams(nextCardId);
      }
    }
  }, [cardId, cards, currentPath, isLoading, replaceWithParams, selectedItemIndex, hash]);
  const getInfoDate = useCallback(
    (date: Date | null) => (date ? getUtcDate(date).format(DEFAULT_DATE_FORMAT) : EMPTY_FIELD_VALUE),
    []
  );

  const shipmentListWithFraudItem = useMemo(() => {
    if (fraudConfirmedShipment) {
      return [fraudConfirmedShipment, ...shipmentList]
        .sort((prev, next) => dayjs(next.date).valueOf() - dayjs(prev.date).valueOf());
    }

    return shipmentList;
  }, [shipmentList, fraudConfirmedShipment]);

  return (
    <Box background="module" round="moduleRound">
      {currentCard ? (
        <CardShipment
          isMobileMode={isMobile}
          footer={
            <CardShipment.Footer>
              <CardShipment.Footer.Item id="1" label="Card Request Date">
                {getInfoDate(currentCard.cardRequestDate)}
              </CardShipment.Footer.Item>
              <CardShipment.Footer.Item id="2" label="Mailed Date">
                {getInfoDate(currentCard.mailedDate)}
              </CardShipment.Footer.Item>
              <CardShipment.Footer.Item id="3" label="Activation Date">
                {getInfoDate(currentCard.activationDate)}
              </CardShipment.Footer.Item>
              <CardShipment.Footer.Item id="4" isLastIndex label="Expiration Date">
                {getInfoDate(currentCard.expDate)}
              </CardShipment.Footer.Item>
            </CardShipment.Footer>
          }
          right={isTrackerLoading ? <Preloader /> : <CardShipment.List list={shipmentListWithFraudItem} />}
        >
          <Box margin={{ right: isMobile ? '' : 'spacing12' }} data-testid="card-details-shipment_card">
            <FiservCard
              key={currentCard.id}
              card={currentCard}
              logos={{
                logoUrl: observingOrganizationData?.logoInfo?.darkLargeLogoUrl,
                cardLogo: whiteLabelingConfig?.images?.cardLogo,
                defaultCardLogo: whiteLabelingConfig?.logo,
                cardBackground: whiteLabelingConfig?.images?.cardBackground,
              }}
              isLoading={isLoading}
              onPushWithParams={handlePushWithParams}
              isFraud={currentCard?.fraudConfirmed}
              wrapperStyle={{}}
            />

            <Box fill direction="row" margin={{ top: 'spacing12' }}>
              <CardActionsCarousel isDarkMode cards={cards} selectedItemIndex={selectedItemIndex} />
            </Box>
          </Box>
          <Box></Box>
        </CardShipment>
      ) : (
        <Box justify="center" align="center">
          <Text color="textSecondary" size="14px" weight={400} margin={{ vertical: 's' }}>
            The employee doesn't have any cards yet.
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default CardCarousel;
